// Material cost from what a unit CONSUMES, instead of from supplier invoices.
//
// A job's material cost only exists today if somebody types every delivery and
// every invoice. Plenty of companies never will, and the tab then reads material
// $0 and the margin lies. The other road: record what ONE unit of a production
// item consumes — block, mortar, mesh, epoxy — and multiply by the quantity the
// crew reported installing.
//
// ═══ THE INVARIANT THIS FILE EXISTS TO KEEP ═══════════════════════════════════
// `projectRecipeCost` NEVER SUMS INSTALLED QUANTITY ITSELF. It is handed the map
// produced by buildInstalledMap (js/core/job-value.js) — the very same map the
// Contract tab earns revenue from and the Production tab shows on screen. Job
// Costs and Contract already drifted apart once on exactly this question (this
// file's caller used to skip rows with a null material_type_id while the
// Contract tab did not), and a third independent walk over production_line_items
// would be the next place they drift. There is one sum, in one function, and
// everything downstream reads its answer.
//
// ═══ THE OTHER RULE: ONE SOURCE, NEVER TWO ═══════════════════════════════════
// A job draws material from deliveries OR from the recipe — never both.
// docs/material-cost-model.md:104 wrote it down before this existed: "Nunca
// deixar os dois alimentarem o mesmo número." `materialCostSplit` below is the
// single gate that enforces it, so the screen and the test cannot disagree about
// which source counted.
//
// Pure arithmetic on plain objects: no DOM, no network, no Supabase.
//
// A recipe line is the shape project_recipe_lines stores, which is also the
// first link this app has ever had between its two material lists:
//   { material_type_id,        production item — what the crew reports building
//     delivery_material_id,    component      — what the crew orders
//     entry_mode,              'covers' | 'uses' — which way round the fact runs
//     entry_value,             the number the owner typed, in that direction
//     unit_price_override }    null = use the price book
// Name and unit are deliberately absent: they belong to the org's own
// catalogue and are looked up by the render layer, never carried here.
//
// ═══ WHY THE CONSUMPTION IS DERIVED AND NOT STORED ═══════════════════════════
// The line used to store the answer — a fraction of an ordered unit per unit of
// work, 0.0014881 pallets per block — and the owner did that division by hand.
// It stores the FACTS now, and this file does the division:
//
//   how the supplier packs it   delivery_materials.pack_size   48 bags/pallet
//   how far one package goes    the line itself                1 bag → 14 blocks
//
// Deriving is the point, not an implementation detail. Correcting the pack size
// on one catalogue row re-costs every recipe in the company at once — the same
// promise the price book already keeps. A stored fraction would need a write
// cascade over every job to do that, and would quietly disagree with the facts
// in between.

import { toCents, footToCents, otherItemKey, excludedKey } from './task-costs.js';

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const nameKey = (s) => String(s == null ? '' : s).trim().toLowerCase();
const finite = (v) => (v === '' || v == null ? null : (Number.isFinite(Number(v)) ? Number(v) : null));

/** The two ways a job can cost its material. Mirrors the CHECK on projects. */
export const MATERIAL_COST_METHODS = Object.freeze(['deliveries', 'recipe']);

/** The two directions a consumption fact can be stated. Mirrors the CHECK on
 *  project_recipe_lines. Both exist because construction says it both ways and
 *  neither is more natural — "a bag lays 14 blocks", "a block takes 2 ties".
 *  They are inverses, so keeping only one would hand the owner back the fraction
 *  he came here to stop calculating. */
export const RECIPE_ENTRY_MODES = Object.freeze(['covers', 'uses']);

/** How many usable pieces one ordered unit holds. Absent, zero or junk all mean
 *  the same thing and the safe thing: the ordered unit IS the usable unit. A
 *  pack size of 0 would divide the world by zero, so it can never come back. */
export function packSizeOf(packSizeById, deliveryMaterialId) {
  const n = Number((packSizeById || {})[deliveryMaterialId]);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/**
 * What one unit of work consumes from this line, in the unit the item is
 * ORDERED and PRICED in — because that is the unit the price book quotes, and
 * cost is this number times that price.
 *
 *   covers   1 bag covers 14 blocks, 48 bags to a pallet
 *              → 1 / (14 × 48)  = 0.0014881 pallets per block
 *   uses     1 block uses 2 ties, 100 ties to a box
 *              → 2 / 100        = 0.02 boxes per block
 *
 * Nothing typed yet returns 0, and 0 is the honest answer: it costs nothing
 * BECAUSE nobody has said. `recipeUnitCost` reports that line as incomplete
 * rather than letting it pass as a component that happens to be free.
 */
export function lineConsumption(line, packSizeById) {
  if (!line) return 0;
  const v = num(line.entry_value);
  if (!(v > 0)) return 0;
  const pack = packSizeOf(packSizeById, line.delivery_material_id);
  return line.entry_mode === 'uses' ? v / pack : 1 / (v * pack);
}

/** The method a project costs by, with anything unrecognised reading as the
 *  behaviour every job had before this feature existed. A project row that
 *  predates the column, or a value nobody knows, must never silently become
 *  "recipe" and blank out a real invoice. */
export function costMethodOf(project) {
  const m = project && project.material_cost_method;
  return m === 'recipe' ? 'recipe' : 'deliveries';
}

/**
 * The same consumption, restated in a different piece.
 *
 * A recipe line names its piece only by implication: with no pack size on the
 * catalogue row, "1 __ covers 14" means one ORDERED unit; with 48 bags to the
 * pallet the same blank means one BAG. So filling that field in for the first
 * time changes what the sentence says without anybody editing the sentence —
 * and 0.001488 pallets per block silently became 0.001488 BAGS per block, a
 * forty-eighth of the cost.
 *
 * This converts the number instead of letting the word change under it: 1.5 m
 * becomes 150 cm, and the wall is the same wall. The owner is teaching the app
 * how his supplier packs a thing, not telling it the crew started using less.
 *
 *   covers   1/(v0 × p0) = 1/(v1 × p1)   →   v1 = v0 × p0/p1
 *   uses     v0/p0       = v1/p1         →   v1 = v0 × p1/p0
 *
 * ONLY the caller decides when this applies, and it applies to exactly one
 * transition: a pack size appearing or disappearing. Correcting 48 to 36 is the
 * other thing entirely — the supplier really did change, each bag really does
 * cost more, and every job that quotes it SHOULD re-price. That case must never
 * come through here.
 *
 * Returns null when there was nothing to restate, so the caller writes nothing.
 */
export function rebasedEntryValue(line, fromPack, toPack) {
  const v = num(line && line.entry_value);
  if (!(v > 0)) return null;
  const p0 = Number(fromPack) > 0 ? Number(fromPack) : 1;
  const p1 = Number(toPack)   > 0 ? Number(toPack)   : 1;
  if (p0 === p1) return null;
  return line.entry_mode === 'uses' ? v * (p1 / p0) : v * (p0 / p1);
}

/**
 * What one component costs, and where that number came from.
 *
 * The price book (delivery_material_prices, typed on Rates & Defaults) is the
 * default so that raising a supplier price in ONE place re-costs every recipe
 * using it. The per-line override exists for the job where the price genuinely
 * differs — and `book` comes back alongside it precisely so the screen can show
 * the book value struck through beside the typed one, which is what makes an
 * overridden price read as set by hand rather than as just another number.
 *
 * `value: null` is a real answer and never a zero: an item with no price
 * anywhere is a hole to be filled, and pricing it at nothing would understate
 * the job silently — the one failure mode this whole feature is meant to end.
 */
export function resolvePrice(line, bookPriceById, freightById) {
  // A free-typed component (custom_name) has no catalog row and no book price
  // to fall back on: its own typed price IS its price, and a missing one is a
  // hole to fill, never a zero.
  if (line && line.custom_name != null) {
    const value = finite(line.custom_unit_price);
    return value != null
      ? { value, source: 'custom', book: null, base: value, freight: 0 }
      : { value: null, source: null, book: null, base: null, freight: 0 };
  }
  const id = line && line.delivery_material_id;
  const book = finite((bookPriceById || {})[id]);
  const override = finite(line && line.unit_price_override);
  // Freight (2026-09-09): the truck is part of the price of a pallet. It is a
  // fact of the supplier typed once on the price book (freightPerUnit below),
  // and it rides on top of the supplier price WHICHEVER one wins — an override
  // says "this job's block costs $290 a pallet", not "and the truck is free".
  const freight = num((freightById || {})[id]);
  if (override != null) return { value: override + freight, source: 'override', book, base: override, freight };
  if (book != null)     return { value: book + freight,     source: 'book',     book, base: book,     freight };
  return { value: null, source: null, book: null, base: null, freight };
}

/**
 * The truck, per ordered unit. A price-book row says "freight $X per load,
 * N units to a load": every pallet on that truck carries X/N of it. A supplier
 * that charges per pallet is the same sentence with N = 1. Nothing typed, or
 * junk, is $0 — the price book behaves exactly as it did before the column.
 */
export function freightPerUnit(row) {
  const per = num(row && row.freight_per_load);
  if (!(per > 0)) return 0;
  const units = num(row && row.units_per_load);
  return per / (units > 0 ? units : 1);
}

/** The group one recipe component's money files under. Every line before this
 *  feature is material, and an unknown value degrades to material — the bucket
 *  the whole recipe has always lived in. */
export function lineTopic(line) {
  const t = line && line.topic_key;
  return typeof t === 'string' && t ? t : 'material';
}

/**
 * What ONE unit of a production item costs in material.
 *
 * Two different holes are reported separately rather than both passing as zero,
 * because they need different fixing and live on different screens:
 *
 *   unpriced    the line says how much is consumed, but the item has no price
 *               anywhere → fill in the price book (Rates & Defaults)
 *   incomplete  the component is in the recipe with no amount typed on it
 *               → say how far one package goes (Cost Recipes)
 *
 * Neither is ever silently costed at nothing, which is the single failure mode
 * this whole feature exists to end.
 *
 * Three more figures ride along, all per unit of work and all inside `cost`:
 *
 *   freight        the truck's share of every landed price (qty × price.freight).
 *                  It files under TRUCKING whatever group its component files
 *                  under (owner's decision, 2026-10-02): `byTopic` holds each
 *                  component's price BEFORE freight under its own group and the
 *                  freight under 'trucking'. byTopic still sums to `cost`.
 *   taxable        every component priced from the price book or an override,
 *                  at its landed price, WHATEVER group it files under. That is
 *                  the sales-tax base: the book quotes before tax, so the app
 *                  adds it. A free-typed component is what the owner paid, tax
 *                  included, and is never in it — so moving a component to
 *                  another group can no longer change the job's total.
 *   typedTrucking  free-typed components filed under Trucking. Next to a
 *                  non-zero `freight` that is the same truck twice (the
 *                  pre-freight workaround: a typed "Delivery" line).
 */
export function recipeUnitCost(lines, bookPriceById, packSizeById, freightById) {
  let cost = 0, taxable = 0, freight = 0, typedTrucking = 0;
  const byTopic = {};
  const unpriced = [];
  const incomplete = [];
  (lines || []).forEach(line => {
    if (!line) return;
    const qty = lineConsumption(line, packSizeById);
    if (!(qty > 0)) {
      incomplete.push({
        materialTypeId: line.material_type_id,
        deliveryMaterialId: line.delivery_material_id,
        customName: line.custom_name || null,
      });
      return;
    }
    const price = resolvePrice(line, bookPriceById, freightById);
    if (price.value == null) {
      unpriced.push({ deliveryMaterialId: line.delivery_material_id,
                      customName: line.custom_name || null, quantity: qty });
      return;
    }
    const lineCost = qty * price.value;
    cost += lineCost;
    const typed = price.source === 'custom';
    if (!typed) taxable += lineCost;
    // The second axis of the same money: which GROUP each component files
    // under. byTopic sums to `cost` by construction — a component's price is
    // counted exactly once in its own bucket, and its freight once in Trucking.
    const topic = lineTopic(line);
    const truck = qty * num(price.freight);
    freight += truck;
    byTopic[topic] = (byTopic[topic] || 0) + (lineCost - truck);
    if (truck) byTopic.trucking = (byTopic.trucking || 0) + truck;
    if (typed && topic === 'trucking') typedTrucking += lineCost;
  });
  return { cost, byTopic, taxable, freight, typedTrucking, unpriced, incomplete };
}

/** project_recipe_lines rows → { material_type_id: [line, …] }, in sort order.
 *  One place does this grouping so the tab that edits the recipe and the tab
 *  that costs from it read the same shape. */
export function groupRecipeLines(rows) {
  const byType = {};
  (rows || []).forEach(r => {
    if (!r || !r.material_type_id) return;
    (byType[r.material_type_id] = byType[r.material_type_id] || []).push(r);
  });
  Object.keys(byType).forEach(k => byType[k].sort((a, b) =>
    (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0)));
  return byType;
}

/**
 * The job's whole material cost by recipe.
 *
 * `installedByType` MUST be buildInstalledMap's output — see the invariant at
 * the top of this file. Nothing here walks production_line_items; this function
 * cannot even see them.
 *
 * One entry in `perItem` per production item the crew actually installed,
 * including the ones with NO recipe on file: those cost 0, and saying so out
 * loud (`hasRecipe: false`) is the difference between "this item is free" and
 * "nobody has told the app what this item consumes yet". An item that HAS a
 * recipe but was never installed contributes nothing and is left out — it
 * belongs on the Cost Recipes tab, not on a statement of what this job spent.
 */
export function projectRecipeCost(recipeByType, installedByType, bookPriceById, packSizeById, freightById) {
  const recipes = recipeByType || {};
  const installed = installedByType || {};
  const perItem = [];
  const unpriced = [];
  const incomplete = [];
  const seenGap = new Set();
  let total = 0;

  // The whole-job fold of byTopic: materialTotal is what the anti-double-count
  // gate governs (it is the recipe's answer to "what did the material cost"),
  // and extraByTopic is every redirected component's money, group by group —
  // real cost that is NOT material and never enters the gate.
  // taxableTotal is the sales-tax base (see recipeUnitCost) and freightTotal
  // the part of `total` that is the truck — it sits inside extraByTopic.trucking.
  let materialTotal = 0, taxableTotal = 0, freightTotal = 0;
  const extraByTopic = {};

  Object.keys(installed).forEach(typeId => {
    const qty = num(installed[typeId]);      // read, never summed here
    if (!qty) return;
    const lines = recipes[typeId] || [];
    const unit = recipeUnitCost(lines, bookPriceById, packSizeById, freightById);
    const cost = qty * unit.cost;
    total += cost;
    taxableTotal += qty * unit.taxable;
    freightTotal += qty * unit.freight;
    const byTopic = {};
    Object.keys(unit.byTopic || {}).forEach(t => {
      const slice = qty * unit.byTopic[t];
      byTopic[t] = slice;
      if (t === 'material') materialTotal += slice;
      else extraByTopic[t] = (extraByTopic[t] || 0) + slice;
    });
    unit.unpriced.forEach(gap => {
      // one component, one nag — a typed component keys by its name
      const key = gap.deliveryMaterialId || 'custom:' + gap.customName;
      if (seenGap.has(key)) return;
      seenGap.add(key);
      unpriced.push(gap);
    });
    // NOT deduped by component, unlike the price gap above: the same component
    // can sit unfilled under two different production items, and each one is a
    // separate sentence somebody has to write.
    unit.incomplete.forEach(gap => incomplete.push({ materialTypeId: typeId, ...gap }));
    perItem.push({
      materialTypeId: typeId,
      installed: qty,
      unitCost: unit.cost,
      cost,
      byTopic,
      materialCost: byTopic.material || 0,
      taxable: qty * unit.taxable,
      freightCost: qty * unit.freight,
      typedTruckingCost: qty * unit.typedTrucking,
      lineCount: lines.length,
      hasRecipe: lines.length > 0,
      unpriced: unit.unpriced,
      incomplete: unit.incomplete,
    });
  });

  // Biggest spend first, then by id so the order is the same on every render —
  // the statement's row order is built from this.
  perItem.sort((a, b) => b.cost - a.cost
    || String(a.materialTypeId).localeCompare(String(b.materialTypeId)));
  return { total, materialTotal, extraByTopic, taxableTotal, freightTotal, perItem, unpriced, incomplete };
}

/**
 * THE ANTI-DOUBLE-COUNT GATE — the one place that decides which source of
 * material cost counts on a job, so nothing downstream can decide it again and
 * differently.
 *
 * Given the delivery side (estimate, invoices, and the sales tax charged on the
 * estimate) and the recipe side, exactly ONE of them survives:
 *   'deliveries' → the deliveries count, the recipe contributes nothing
 *   'recipe'     → the recipe counts, deliveries and their tax contribute nothing
 * `countsDeliveries` / `countsRecipe` are what the render layer dims the losing
 * rows with. The rows themselves STAY on screen: the delivery really happened,
 * so hiding it would lose information — while counting it would count the same
 * material twice.
 *
 * Sales tax follows the estimate that is LIVE, and the losing side's tax dies
 * with it. The price book quotes before tax on BOTH roads (owner's rule,
 * 2026-08-02: prices are always entered without PST, the app adds it), so the
 * recipe estimate pays PST exactly like the delivery estimate does — the caller
 * hands each side its own tax and this gate keeps exactly one of them.
 *
 * Each side is handed as its MATERIAL slice: the freight inside a landed price
 * files under Trucking on both roads (2026-10-02), so the caller keeps it out
 * of `estimated` / `recipeTotal` and adds it to Trucking itself. The TAX it is
 * handed is still the tax on the landed figure — freight is taxed, it is only
 * filed elsewhere.
 *
 * Typed cost entries (job_cost_entries) are NOT part of this gate. An invoice
 * the office typed is its own feed and is real on either method — it is neither
 * a delivery nor a recipe.
 *
 * OTHER ITEM DELIVERIES (2026-10-03). The rule above is about the SAME material
 * reaching the total twice. A delivered item that no recipe line names is not
 * the recipe's material at all, so on the recipe road it counts beside it:
 * `deliveries.outside` is its price before freight and `deliveries.outsidePst`
 * the sales tax on its landed price (otherItemDeliveries, below). Both are read
 * on the recipe method only — on the deliveries method every delivery already
 * counts whole, and they are ignored.
 */
export function materialCostSplit(method, deliveries, recipeTotal, recipePst) {
  const m = method === 'recipe' ? 'recipe' : 'deliveries';
  const d = deliveries || {};
  const useRecipe = m === 'recipe';
  const estimated = useRecipe ? 0 : num(d.estimated);
  const invoiced  = useRecipe ? 0 : num(d.invoiced);
  const outside   = useRecipe ? num(d.outside) : 0;
  const pst       = useRecipe ? num(recipePst) + num(d.outsidePst) : num(d.pst);
  const recipe    = useRecipe ? num(recipeTotal) : 0;
  return {
    method: m,
    estimated, invoiced, pst, recipe, outside,
    total: estimated + invoiced + pst + recipe + outside,
    countsDeliveries: !useRecipe,
    countsRecipe: useRecipe,
  };
}

/** Sales tax on the recipe's TAXABLE base (projectRecipeCost.taxableTotal: every
 *  component priced from the price book or an override, freight included,
 *  whatever group it files under; never a free-typed one). One multiplication,
 *  in one place, because two screens print it: Job Costs charges it, Cost
 *  Recipes shows it. */
export function recipeSalesTax(taxableTotal, pstPct) {
  return num(taxableTotal) * num(pstPct);
}

/**
 * What a job's recipe cost is made of, in whole cents that re-add:
 *
 *   material          the components filed under Material, before tax and
 *                     before freight
 *   taxBase           what the tax is charged on: every price-book / override
 *                     component at its landed price, in ANY group — printed
 *                     beside the tax so "7% of $X" multiplies out
 *   tax               sales tax on taxBase (exactly what Job Costs charges)
 *   materialWithTax   material + tax — what the gate hands Job Costs as
 *                     Material while nobody has re-filed those rows there
 *   extra             [{ topic, amount }] — components filed under other
 *                     groups; 'trucking' carries the price-book freight
 *   total             materialWithTax + every extra
 *
 * `other` is otherItemDeliveries' answer (null on a job with no recipe line):
 * its items before freight are a row of their own (`other`), inside
 * materialWithTax; their landed price joins taxBase and their freight joins the
 * 'trucking' extra — the same three places Job Costs puts them on a recipe job.
 * The Cost Recipes tab hands it over on a job still on deliveries too, where
 * the whole breakdown is a what-if: the figure it promises before the switch
 * has to be the figure in force after it.
 *
 * The tax goes through materialCostSplit rather than being added here, so the
 * Cost Recipes tab can never tax a recipe the gate would not.
 *
 * Cents: the recipe before tax (material + every extra) is the fact the same
 * screen prints again as "Recipe to date" on the deliveries card, so those rows
 * are fitted to THAT figure and the tax row is whatever is left of the total.
 * Fitting all the rows to the total at once let the material row give up the
 * spare cent — "before sales tax $49,072.80" over "Recipe to date $49,072.81".
 */
export function recipeBreakdown(costed, pstPct, other) {
  const c = costed || {};
  const o = other || {};
  const split = materialCostSplit('recipe',
    { outside: num(o.landed) - num(o.freight), outsidePst: recipeSalesTax(o.landed, pstPct) },
    c.materialTotal, recipeSalesTax(c.taxableTotal, pstPct));
  const raw = { material: split.recipe };
  if (split.outside) raw.other = split.outside;
  Object.keys(c.extraByTopic || {}).forEach(t => { raw['x:' + t] = num(c.extraByTopic[t]); });
  if (num(o.freight)) raw['x:trucking'] = num(raw['x:trucking']) + num(o.freight);
  const beforeTax = Object.values(raw).reduce((s, v) => s + v, 0);
  // All the rows are fitted to the figure before tax AT ONCE, the way Job Costs
  // fits its groups to Direct — which is what keeps "Material incl. sales tax"
  // and the Trucking row here on the cent Job Costs prints. The price: when an
  // other item is set to "not a cost" the spare cent can change rows, and
  // "Material, from the recipe" may read a cent apart ($69,476.17 / .18) with
  // no recipe figure changed. Fitting the recipe's rows on their own keeps that
  // row still and puts Material and Trucking a cent from Job Costs on two real
  // jobs at all times (tried 2026-10-03) — the worse of the two.
  const fit = footToCents(raw, toCents(beforeTax));
  const total = toCents(beforeTax + split.pst);
  const tax = toCents(total - toCents(beforeTax));
  const extra = Object.keys(fit).filter(k => k.startsWith('x:') && fit[k] !== 0)
    .map(k => ({ topic: k.slice(2), amount: fit[k] }));
  const otherFit = fit.other || 0;
  return { material: fit.material, other: otherFit, taxBase: toCents(num(c.taxableTotal) + num(o.landed)), tax,
           materialWithTax: toCents(fit.material + otherFit + tax), extra, total };
}

/** The job's recipe figure (recipeBreakdown().total) straight from recipe rows.
 *  The Cost Recipes total and the "what will this click do" previews (Use,
 *  Export) all come from here, so a preview and the total after it agree. */
export function recipeJobTotal({ lines, installed, bookPriceById, packSizeById, freightById, pstPct, other }) {
  const costed = projectRecipeCost(groupRecipeLines(lines), installed, bookPriceById, packSizeById, freightById);
  return recipeBreakdown(costed, pstPct, other).total;
}

// ═══ THE MEASURED RECIPE ═════════════════════════════════════════════════════
// 2026-09-09, the owner's goal in one sentence: "chegar a um valor VERDADEIRO
// de recipe por bloco, sem eu ter que perguntar informação ao site".
//
// The recipe above is what the owner BELIEVES one unit consumes. The app also
// knows two things nobody has to be asked for: what the job took delivery of
// (delivery_requests, status delivered) and what the crew reported installing
// (the same installed map the Contract tab earns from). Divide one by the other
// and the answer includes every broken block and every bag dropped on the
// ground — the waste a typed recipe never sees.
//
// It also includes the material still sitting on site, so mid-job it reads
// HIGH and converges on the truth as the job is finished. That is said out loud
// on screen, not hidden: this is an honest number with a known bias, which is
// worth more than a guessed number with none.
//
// A component shared by two production items (mortar under both 8" and 6"
// block) has one delivered pile and two consumers. The pile is split in the
// proportion the recipe EXPECTS each item to have used (its stated consumption
// × its installed quantity); with nothing stated on either, by installed
// quantity alone. A better split needs a fact the site would have to be asked
// for, which is exactly what this feature exists to avoid.

// ── Which catalogue row an "extra" delivery item is ─────────────────────────
// An extra_materials entry carries the catalogue row's id when the picker knew
// it (delivery_material_id, written since 2026-10-01). Every older entry — and
// anything picked where no catalogue row exists — carries only the NAME, and a
// catalogue may hold several rows with one name (the same item in two trade
// packs, one shape in four sizes). The name used to resolve "last row read
// wins", so the answer depended on the order the database returned the rows
// in. These three functions are the one lookup every reader uses, and none of
// them depends on row order.

/** The catalogue, indexed for that lookup. */
export function extraMaterialIndex(catalog) {
  const byId = {}, byName = {};
  (catalog || []).forEach(r => {
    if (!r || !r.id) return;
    byId[r.id] = r;
    const k = nameKey(r.name);
    if (k) (byName[k] = byName[k] || []).push(r.id);
  });
  return { byId, byName };
}

/** Every catalogue row one extra entry can mean: its own id when it carries
 *  one that still exists, otherwise every row with its name ([] = none). */
export function extraMaterialIds(x, index) {
  const ix = index || {};
  const id = x && x.delivery_material_id;
  if (id && (ix.byId || {})[id]) return [id];
  return (ix.byName || {})[nameKey(x && x.name)] || [];
}

/**
 * The price of one extra entry: { price, freight, ambiguous }.
 *
 * `landedById` is { catalogue id: price incl. freight }. Same-named rows that
 * agree on the price (or of which only one is priced) are not a problem and
 * price normally. Rows that DISAGREE are: nothing on the entry says which one
 * was meant, so it comes back unpriced and `ambiguous` — the screen says so
 * instead of picking one.
 *
 * `freight` is the truck's share of that price (`freightById`), the slice that
 * files under Trucking. Same-named rows that agree on the landed price but not
 * on how much of it is freight give 0: the price is certain, the split is not,
 * and the money then simply stays under Material. Never order-dependent.
 */
export function extraMaterialPrice(x, index, landedById, freightById) {
  const prices = [], freights = [];
  extraMaterialIds(x, index).forEach(id => {
    const p = Number((landedById || {})[id]);
    if (!(Number.isFinite(p) && p > 0)) return;
    if (!prices.some(q => Math.abs(q - p) < 1e-9)) prices.push(p);
    const f = num((freightById || {})[id]);
    if (!freights.some(q => Math.abs(q - f) < 1e-9)) freights.push(f);
  });
  if (prices.length === 1) return { price: prices[0], freight: freights.length === 1 ? freights[0] : 0, ambiguous: false };
  return { price: null, freight: 0, ambiguous: prices.length > 1 };
}

/**
 * Has this request ARRIVED? The one definition of "delivered", shared by the
 * measured recipe below (delivered ÷ installed) and by Job Costs, where on the
 * deliveries method only what arrived is money (owner's decision, 2026-10-02).
 * An allow-list on purpose: requested, on_schedule, on_hold and cancelled — and
 * any status invented later — have put nothing on the ground.
 */
export function isDelivered(request) {
  return !!request && request.status === 'delivered';
}

/** delivery_requests rows → { delivery_material_id: ordered units delivered }.
 *  The request stores its items as a jsonb keyed the way Job Costs already
 *  reads it: nested {category: {type_key: qty}}, root scalars for the
 *  category-less items (mortar_tek, blockfill), and extra_materials by id or,
 *  for older rows, by name (extraMaterialIds). An extra whose name fits more
 *  than one catalogue row is left out: crediting the pile to whichever row was
 *  read last would measure the wrong recipe.
 *  Only delivered requests count — a request still on the road has put nothing
 *  on the ground. */
export function deliveredUnitsByMaterial(requests, catalog) {
  const byKey = {};
  (catalog || []).forEach(r => {
    if (!r || !r.id) return;
    if (r.type_key) byKey[(r.category || 'materials') + '|' + r.type_key] = r.id;
  });
  const index = extraMaterialIndex(catalog);
  const out = {};
  const add = (id, qty) => { if (id && qty > 0) out[id] = (out[id] || 0) + qty; };
  (requests || []).forEach(rq => {
    if (!isDelivered(rq)) return;
    const items = rq.items || {};
    Object.entries(items).forEach(([cat, val]) => {
      if (cat === 'extra_materials' && Array.isArray(val)) {
        val.forEach(x => {
          const ids = extraMaterialIds(x, index);
          if (ids.length === 1) add(ids[0], num(x && x.qty));
        });
        return;
      }
      if (cat === 'other_materials') return;
      if (typeof val === 'number') { add(byKey['materials|' + cat], val); return; }
      if (!val || typeof val !== 'object') return;
      Object.entries(val).forEach(([key, qty]) => add(byKey[cat + '|' + key], num(qty)));
    });
  });
  return out;
}

/**
 * What the job's own deliveries say each line REALLY consumes.
 *
 * Returns { [line.id]: { units, installed, perUnit, entryValue } } for every
 * catalog line whose component was delivered on a job with installed work:
 *   units       ordered units of the component allocated to this line
 *   installed   units of work the line's production item reported
 *   perUnit     ordered units per unit of work — the measured lineConsumption
 *   entryValue  the same fact restated in the line's own sentence
 *               ('covers': 1 piece covers N; 'uses': 1 unit uses N pieces), so
 *               "Use measured" writes into the box the owner already reads.
 * A line with nothing delivered, or no installed work, is simply absent: there
 * is no measurement, and an absent measurement must never read as zero.
 */
export function measuredConsumption(recipeByType, installedByType, deliveredById, packSizeById) {
  const recipes = recipeByType || {};
  const installed = installedByType || {};
  const delivered = deliveredById || {};
  // Every consumer of each component, with the weight the recipe expects.
  const consumers = {};
  Object.keys(recipes).forEach(typeId => {
    const qty = num(installed[typeId]);
    if (!(qty > 0)) return;
    (recipes[typeId] || []).forEach(line => {
      if (!line || line.custom_name != null || !line.delivery_material_id) return;
      const expected = lineConsumption(line, packSizeById) * qty;
      (consumers[line.delivery_material_id] = consumers[line.delivery_material_id] || [])
        .push({ line, typeId, installed: qty, expected });
    });
  });
  const out = {};
  Object.keys(consumers).forEach(dmId => {
    const units = num(delivered[dmId]);
    if (!(units > 0)) return;
    const list = consumers[dmId];
    const sumExpected = list.reduce((s, c) => s + c.expected, 0);
    const sumInstalled = list.reduce((s, c) => s + c.installed, 0);
    list.forEach(c => {
      const share = sumExpected > 0 ? c.expected / sumExpected : c.installed / sumInstalled;
      const mine = units * share;
      if (!(mine > 0)) return;
      const perUnit = mine / c.installed;
      const pack = packSizeOf(packSizeById, dmId);
      const entryValue = c.line.entry_mode === 'uses' ? perUnit * pack : 1 / (perUnit * pack);
      out[c.line.id] = { units: mine, installed: c.installed, perUnit, entryValue };
    });
  });
  return out;
}

// ═══ DELIVERIES AGAINST THE RECIPE ═══════════════════════════════════════════
// On the recipe method the deliveries stop counting — that is the rule, and it
// is not re-opened here. What the rule hid is that the two can be COMPARED:
// the app knows what landed on the job and what the recipe says the installed
// work consumed, so it can say where they differ and why. Two different things
// live in that difference and they must not be read as one:
//
//   in the recipe    delivered more (or less) of a component than the recipe
//                    has used so far. Mid-job that is mostly stock on site —
//                    expected, and not an error in the cost.
//   outside it       delivered material NO recipe line names. That is no longer
//                    part of this comparison: since 2026-10-03 it is COST, its
//                    own item ("Other item deliveries" — otherItemDeliveries,
//                    below), so it is left out of both sides here.
//
// Reports; never moves a figure.

/**
 * @param o.deliveredById   deliveredUnitsByMaterial's answer (delivered only)
 * @param o.recipeLines     every project_recipe_lines row of the job — a line
 *                          filed under another group, or under an item not
 *                          installed yet, still counts as "the recipe names it"
 * @param o.installedByType buildInstalledMap's answer
 * @param o.catalog         delivery_materials rows, active or not
 * @param o.priceById / o.freightById / o.packSizeById   the price book
 * @param o.recipeTotal     projectRecipeCost(...).total — before tax
 *
 * Money is LANDED price-book money (price + freight), before sales tax — the
 * recipe's own basis.
 *
 * Returns
 *   deliveredValue   what was delivered OF THE COMPONENTS THE RECIPE NAMES, at
 *                    its price-book price
 *   recipeTotal      the recipe total it was handed, in cents
 *   difference       deliveredValue − recipeTotal
 *   components       [{ id, name, unit, delivered, used, units, value }] — what
 *                    the recipe names, where delivered ≠ used
 *   repriced         the rest of the difference, on a recipe with lines priced
 *                    BY HAND (a typed price, a typed component). Always 0 on a
 *                    recipe priced from the book — what is left over there is
 *                    rounding, and rounding is not a row.
 * Every figure is in cents, and Σ components.value + repriced is `difference`
 * exactly. The cents rounding leaves over go to the component rows
 * (footToCents). Only when no component row exists to take them do the two
 * totals give way instead — by that cent — so the card still re-adds.
 */
export function deliveriesVsRecipe(o) {
  const i = o || {};
  const delivered = i.deliveredById || {};
  const installed = i.installedByType || {};
  const byId = {};
  (i.catalog || []).forEach(r => { if (r && r.id) byId[r.id] = r; });
  const landed = (id) => {
    const p = finite((i.priceById || {})[id]);
    return p == null ? null : p + num((i.freightById || {})[id]);
  };

  // What the recipe names, and how much of each it says the installed work used.
  const named = recipeNames(i.recipeLines), used = {};
  (i.recipeLines || []).forEach(l => {
    if (!l || l.custom_name != null || !l.delivery_material_id) return;
    const qty = num(installed[l.material_type_id]);
    if (qty > 0) used[l.delivery_material_id] = (used[l.delivery_material_id] || 0)
      + lineConsumption(l, i.packSizeById) * qty;
  });

  let deliveredValue = 0;
  const raw = {}, components = [];
  new Set([...Object.keys(delivered), ...Object.keys(used)]).forEach(id => {
    const row = byId[id] || {};
    // In no recipe line: not this card's — it is cost (otherItemDeliveries).
    if (!named.ids.has(id) && !named.names.has(nameKey(row.name))) return;
    const qty = num(delivered[id]);
    const price = landed(id);
    if (price != null) deliveredValue += qty * price;
    // A difference worth less than half a cent explains nothing — it is what
    // is left of a recipe amount stored to six decimals, not material.
    const units = qty - num(used[id]);
    if (price == null || Math.abs(units * price) < 0.005) return;
    raw['c:' + id] = units * price;
    components.push({ id, name: row.name || '', unit: row.unit || '',
                      delivered: qty, used: num(used[id]), units });
  });
  // The three printed figures subtract on a calculator: the difference is the
  // two rounded totals apart, and the rows under it are fitted to THAT.
  const recipeTotal = toCents(i.recipeTotal);
  let delivValue = toCents(deliveredValue);
  let difference = toCents(delivValue - recipeTotal);
  // A recipe priced from the book differs from the deliveries by QUANTITY only,
  // and every quantity is a row above. What is then left is rounding (sub-cent
  // rows not listed, two totals rounded apart) — never a row of its own.
  const byHand = (i.recipeLines || []).some(l => l
    && (l.custom_name != null || finite(l.unit_price_override) != null));
  const rest = deliveredValue - num(i.recipeTotal)
    - Object.values(raw).reduce((s, v) => s + v, 0);
  raw.repriced = byHand && Math.abs(rest) >= 0.005 ? rest : 0;
  const fit = footToCents(raw, difference);
  components.forEach(c => { c.value = fit['c:' + c.id]; });
  const rows = toCents(Object.values(fit).reduce((s, v) => s + v, 0));
  if (rows !== difference) {
    // Nothing could take the leftover cent(s): the rows are the facts, so the
    // total follows them.
    difference = rows;
    delivValue = toCents(recipeTotal + rows);
  }
  const bySize = (a, b) => Math.abs(b.value || 0) - Math.abs(a.value || 0)
    || String(a.name).localeCompare(String(b.name)) || String(a.id).localeCompare(String(b.id));
  return {
    deliveredValue: delivValue,
    recipeTotal,
    difference,
    components: components.sort(bySize),
    repriced: fit.repriced,
  };
}

/** One deliveriesVsRecipe row that is off by more than half of what the recipe
 *  says was used: 'short' (less landed than used — a delivery missing from the
 *  app, or a recipe amount too generous) or 'over' (more landed — stock still
 *  on site, or a recipe amount too low). null when within half, or nothing is
 *  used yet (there is nothing to measure against). */
export function deliveryGap(delivered, used) {
  const u = num(used), dl = num(delivered);
  if (!(u > 0)) return null;
  if (dl < u * 0.5) return 'short';
  if (dl > u * 1.5) return 'over';
  return null;
}

// ═══ OTHER ITEM DELIVERIES ═══════════════════════════════════════════════════
// Owner's decision, 2026-10-03: "items that are in deliveries and not in the
// recipes: let's create an item in the recipes for 'Other item deliveries'".
//
// On a job that costs material by recipe, what landed that NO recipe line names
// reached no total — it was listed, valued in a warning, and never counted. It
// is cost now: its own item beside the production items, and the one thing on
// the recipe road that is costed from the DELIVERIES — the quantity delivered ×
// the price-book price, delivery by delivery, on the day each arrived.
//
// It reuses the deliveries road whole. The caller estimates every request with
// the estimator that road already uses, and this only decides which of its
// lines are the recipe's (not counted — the recipe costs them by installed
// quantity) and which are nobody's (counted here). So the 2026-10-02 decisions
// hold with no second implementation: only a DELIVERED request is money, the
// freight inside a landed price files under Trucking, and sales tax is charged
// on what the price book priced.
//
// NEVER BOTH. A line is the recipe's as soon as ANY recipe line of the job —
// under any production item, installed or not, filed under any group — names
// its catalogue row. Adding the line takes the item out of here; removing it
// puts it back. Nothing is stored: this is derived on every read.
//
// NOT A COST (owner's decision, 2026-10-03). "Named by no recipe line" is not
// always "paid by no recipe line": a recipe that charges every installed unit
// as ONE standard component already pays for the special ones delivered beside
// it, and counting those here counts them twice. The recipe cannot say so (it
// does not name them), so the owner does, per item and per job:
// projects.other_delivery_excluded holds the keys of the items that are
// "already in a recipe: do not count" — the key each of their delivery lines
// carries (excludedKey: the catalogue row, or the name of a line known by name
// alone). An excluded item is in NO
// figure — not its material, not its share of the truck, not the sales-tax
// base, not an order still to arrive, not a "no price" warning. It stays on
// the list, marked, with the value it would have, so it can be put back.

/** What the job's recipe names: catalogue rows by id, typed components by name. */
export function recipeNames(recipeLines) {
  const ids = new Set(), names = new Set();
  (recipeLines || []).forEach(l => {
    if (!l) return;
    if (l.custom_name != null) names.add(nameKey(l.custom_name));
    else if (l.delivery_material_id) ids.add(l.delivery_material_id);
  });
  return { ids, names };
}

/**
 * @param o.deliveries   [{ id, status, est }] — every request of the job with
 *                       the deliveries road's own estimate of it: est.lines
 *                       (priced: { key, label, qty, unit, cost, freight, ids })
 *                       and est.unpriced ({ label, qty, unit, ambiguous?, ids }).
 *                       `ids` are the catalogue rows the entry can mean: one
 *                       for a built-in item or an extra that carries its id,
 *                       every same-named row for an older extra, none for
 *                       something the catalogue does not hold.
 * @param o.recipeLines  every project_recipe_lines row of the job
 * @param o.catalog      delivery_materials rows, active or not
 * @param o.excluded     projects.other_delivery_excluded — the keys set to
 *                       "not a cost" on this job ([] / absent: none). A line is
 *                       out when the list holds the key the line itself carries
 *                       (excludedKey: its catalogue row, or — an entry known by
 *                       name alone — that name). It is a fact about the LINE:
 *                       it does not move when the catalogue is edited on
 *                       another screen, nor with which other lines are in the
 *                       run, so an as-of day and today always agree. The Cost
 *                       Recipes tab stores that key for every line of a row it
 *                       excludes, beside the row's own.
 *
 * Which entries are "other":
 *   · on a DELIVERED request (isDelivered). The supplier invoice typed on a
 *     request is not read: it is one amount for the whole order, part of which
 *     may be the recipe's, so these items count at the price book like every
 *     other one — never lost, and never on top of an invoice;
 *   · identified — at least one catalogue row. Free text is not something the
 *     price book can price, and stays out, as it always has on this road;
 *   · named by no recipe line — none of its rows, and no typed component of
 *     the same name. An older extra whose name fits several rows is the
 *     recipe's as soon as ONE of them is named: never counted twice.
 * A priced one is money. One with no price costs $0 and is listed, so it can
 * be priced; one whose same-named rows disagree on the price (`ambiguous`,
 * extraMaterialPrice) is listed the same way and named apart.
 *
 * Returns null on a job with no recipe line at all — there is nothing to be
 * "other" than, and that job behaves as it did. Otherwise:
 *   byDelivery  { [request id]: { lines, unpriced, total, freight, covered,
 *               excluded } } for each delivered request — its estimate cut
 *               down to the other items THAT COUNT (the same shape as est, so
 *               the statement, the freight row, the tax and the per-item task
 *               filing read it exactly as they read a whole delivery), how many
 *               of its entries the recipe covers, and `excluded`: its entries
 *               set to "not a cost", priced or not — in none of the figures
 *   pending     the same cut, by request id, for every request that has NOT
 *               arrived — what would count once it is set to Delivered. It is
 *               in no figure below: it is how the screen says what a late
 *               order is withholding. The status alone decides (an invoice is
 *               not read on this road, so it does not make an order "arrived").
 *   items       [{ key, id, name, unit, category, qty, price, freight,
 *               value|null, ambiguous, excluded? }] — one row per catalogue item, all
 *               deliveries together, biggest first, the unpriced last (an item
 *               whose lines are part counted and part "not a cost" — a list
 *               stored before a catalogue edit merged two rows — is two rows
 *               under one key, each with its own lines). `key` is
 *               otherItemKey's: what a task picked for the row is stored under
 *               (js/core/task-costs.js). `value` is LANDED
 *               (price + freight) before sales tax, in cents, and the rows add
 *               up to `total`; null = no price, never $0. `price` is the
 *               PRICE-BOOK price of one unit, before freight — the figure on
 *               Rates & Defaults — and `freight` the truck inside `value`, in
 *               cents, taken from the printed value so that
 *               price × qty + freight = value on every row.
 *               A row set to "not a cost" carries `excluded: true` and the
 *               value it WOULD have; it is in none of the figures below, and
 *               those rows add up to `notCounted.total` instead.
 *   landed      Σ of the priced lines, unrounded — the sales-tax base
 *   freight     the part of `landed` that is the truck, unrounded
 *   total       `landed` in cents
 *   freightInTotal   the truck inside `total`, in cents: `total` less the
 *               price-book part. It is what the header prints beside `total`,
 *               and (prices being whole cents) what the rows' freight adds up
 *               to — rounding `freight` on its own could land a cent away from
 *               both when the landed prices carry half cents.
 *   priced / unpriced   how many rows of each (an ambiguous one is unpriced; a
 *               `noCat` row — deliveredCatalogueItems, below — is neither)
 *   ambiguous   [{ name, unit, qty }] — the rows the catalogue cannot tell apart
 *               (all three: of the rows that count — an excluded item asks for
 *               no price)
 *   notCounted  { items, total } — how many rows are set to "not a cost" and
 *               what they would add, landed and before sales tax, in cents:
 *               `total` + notCounted.total is, to the cent, the `total` of the
 *               same job with nothing excluded
 */
export function otherItemDeliveries(o) {
  const i = o || {};
  if (!(i.recipeLines || []).some(Boolean)) return null;
  return cutDeliveries(i, recipeNames(i.recipeLines), i.excluded);
}

/**
 * "Not a cost" on the deliveries road (2026-11-02, material = what came in):
 * one request's estimate with the entries the job set to "not a cost" cut out
 * — asked of each line by the key it carries itself (excludedKey), like
 * cutDeliveries below, so no catalogue edit puts one back. `est` is
 * _jcEstimateDelivery's { lines, unpriced, total, freight }; the cut keeps that
 * shape (total and freight re-summed over the lines left) and is `est` itself
 * when nothing is cut. `excluded` is what was cut, priced lines with their cost.
 */
export function cutExcluded(est, excludedKeys) {
  const e = est || { lines: [], unpriced: [], total: 0, freight: 0 };
  const shut = new Set(excludedKeys || []);
  // A line with no catalogue row is cut too when it is known by its name (an
  // extra no row has: its key is that name and unit, which nothing moves). A
  // built-in slot with no row has no key of its own to keep, and is never cut.
  const off = (l) => ((l.ids || []).length > 0 || !!l.byName) && shut.has(excludedKey(l));
  const excluded = shut.size ? [...(e.lines || []), ...(e.unpriced || [])].filter(off) : [];
  if (!excluded.length) return { est: e, excluded };
  const lines = (e.lines || []).filter(l => !off(l));
  return { excluded, est: { ...e, lines, unpriced: (e.unpriced || []).filter(l => !off(l)),
    total: lines.reduce((s, l) => s + num(l.cost), 0), freight: lines.reduce((s, l) => s + num(l.freight), 0) } };
}

/**
 * The same list with NOTHING named: every catalogue item on a delivered
 * request. It is what a job set to cost by recipe holds in no total while it
 * has no recipe line yet — otherItemDeliveries is null there, every delivery
 * is at zero, and this is how the screen can still say how much that is.
 * Same shape as otherItemDeliveries. Reports; never counted.
 *
 * Since 2026-11-02 it is also the "Delivered items" list of the Material by
 * task tab (every item that came in, with the task it files to); `o.excluded`
 * marks the rows set to "not a cost" there, exactly as otherItemDeliveries does.
 */
export function deliveredCatalogueItems(o) {
  return cutDeliveries(o || {}, recipeNames([]), (o || {}).excluded, true);
}

// `byName`: also list an extra the catalogue does not hold, known by its name
// (rented scaffold requested through Logistics, a row deleted since) — a row
// `noCat`, keyed as "not a cost" stores it (excludedKey), with no price to ask
// for. Only the Material by task list: the recipe road's cut is as it was.
function cutDeliveries(i, named, excludedKeys, byName) {
  const byId = {};
  (i.catalog || []).forEach(r => { if (r && r.id) byId[r.id] = r; });
  const covered = (e) => (e.ids || []).some(id =>
    named.ids.has(id) || named.names.has(nameKey((byId[id] || {}).name)));
  const mine = (e) => ((e.ids || []).length > 0 && !covered(e)) || (!!byName && !!e.byName && !(e.ids || []).length);
  const entries = (est) => [...(est.lines || []), ...(est.unpriced || [])];
  // Set to "not a cost": asked of the line alone, by the key it carries itself
  // (excludedKey). Neither the catalogue of the day nor any other line of the
  // job is consulted, so a line is out in every run or in none.
  const shut = new Set(excludedKeys || []);
  const off = (e) => shut.has(excludedKey(e));
  const counts = (e) => mine(e) && !off(e);
  const cut = (est) => {
    const lines = (est.lines || []).filter(counts);
    return {
      lines, unpriced: (est.unpriced || []).filter(counts),
      total: lines.reduce((s, l) => s + num(l.cost), 0),
      freight: lines.reduce((s, l) => s + num(l.freight), 0),
      covered: entries(est).filter(covered).length,
      excluded: entries(est).filter(e => mine(e) && off(e)),
    };
  };
  // What a request's `total` is with nothing excluded — summed exactly the way
  // that run sums it, so toCents(whole) below IS the header that run prints.
  const wholeOf = (est) => (est.lines || []).filter(mine).reduce((s, l) => s + num(l.cost), 0);

  const byDelivery = {}, pending = {}, rows = {};
  let landed = 0, freight = 0, whole = 0;
  // Summed in ONE order, whatever order the caller read the requests in: Job
  // Costs reads them newest first and Cost Recipes by id, a landed price is
  // often a half-cent figure ($277.20 + $28.125 of freight), and a float sum
  // taken in two orders can round to two different cents.
  const idOf = (d) => String(d && d.id);
  (i.deliveries || []).slice().sort((a, b) => (idOf(a) < idOf(b) ? -1 : idOf(a) > idOf(b) ? 1 : 0)).forEach(d => {
    const out = cut(d.est || {});
    if (!isDelivered(d)) { pending[d.id] = out; return; }
    const { lines, unpriced } = out;
    byDelivery[d.id] = out;
    landed += out.total;
    freight += out.freight;
    whole += wholeOf(d.est || {});
    const add = (e, priced, excluded) => {
      const one = e.ids.length === 1 ? byId[e.ids[0]] : null;
      // One row per ITEM — the key the per-item task rule is stored under, so a
      // rule picked on a row files every line that lands on it (otherItemKey).
      // The lines of one item that are "not a cost" are a row of their own
      // beside the ones that count: a row never mixes the two.
      const key = otherItemKey(e), slot = (excluded ? '\u0000' : '') + key;
      const r = rows[slot] || (rows[slot] = {
        key, id: one ? one.id : null, name: (one && one.name) || e.label || '',
        unit: (one && one.unit) || e.unit || '', category: (one && one.category) || '',
        qty: 0, raw: null, freight: 0, ambiguous: false, excluded, noCat: !e.ids.length });
      r.qty += num(e.qty);
      if (priced) { r.raw = num(r.raw) + num(e.cost); r.freight += num(e.freight); }
      else if (e.ambiguous) r.ambiguous = true;
    };
    lines.forEach(l => add(l, true, false));
    unpriced.forEach(l => add(l, false, false));
    // Still a row of the list, with what it would be worth.
    out.excluded.forEach(l => add(l, l.cost != null, true));
  });

  const total = toCents(landed);
  // "Not counted" is what is LEFT of the header the job has with nothing
  // excluded — never a figure rounded on its own, which could sit a cent from
  // it: the header drops by exactly what the list says is not counted.
  const offTotal = toCents(toCents(whole) - total);
  const keys = Object.keys(rows);
  // The rows that count are fitted to the header's total; the excluded ones to
  // their own, so "N not counted ($X)" adds up too.
  const fitTo = (excluded, sum) => footToCents(Object.fromEntries(keys
    .filter(k => rows[k].excluded === excluded && rows[k].raw != null).map(k => [k, rows[k].raw])), sum);
  const fit = { ...fitTo(false, total), ...fitTo(true, offTotal) };
  const items = keys.map(k => {
    const r = rows[k];
    return { key: r.key, id: r.id, name: r.name, unit: r.unit, category: r.category, qty: r.qty,
             price: r.raw != null && r.qty > 0 ? (r.raw - r.freight) / r.qty : null,
             // The truck inside the row is what is left of its PRINTED value once
             // the price-book part is taken out — so the row closes on a
             // calculator (price × qty + freight = value) whichever way its
             // half cent was fitted.
             freight: r.freight > 0 ? toCents(fit[k] - (r.raw - r.freight)) : 0,
             value: r.raw != null ? fit[k] : null, ambiguous: r.raw == null && r.ambiguous,
             ...(r.excluded ? { excluded: true } : {}), ...(r.noCat ? { noCat: true } : {}) };
  }).sort((a, b) => (a.value == null) - (b.value == null) || (b.value || 0) - (a.value || 0)
    || String(a.name).localeCompare(String(b.name)) || String(a.id).localeCompare(String(b.id))
    || !!a.excluded - !!b.excluded);                    // one item in two rows: the part that counts first
  const live = items.filter(it => !it.excluded);
  const priced = live.filter(it => it.value != null).length;
  return {
    byDelivery, pending, items, landed, freight, total,
    freightInTotal: freight > 0 ? toCents(total - (landed - freight)) : 0,
    priced, unpriced: live.filter(it => it.value == null && !it.noCat).length,
    ambiguous: live.filter(it => it.ambiguous).map(it => ({ name: it.name, unit: it.unit, qty: it.qty })),
    notCounted: { items: items.length - live.length, total: offTotal },
  };
}
